import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Upload, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shared application form used by both:
 *   - /apply/doctor  (type = "doctor_pharmacist")  → specialty is required
 *   - /apply/jobs    (type = "job")                 → job_title is required
 *
 * Design brief said "do not redesign the footer" — the form itself follows
 * the current Tababa card-in-hero styling.
 */
export const ApplicationFormPage = ({ variant }) => {
  const isDoctor = variant === 'doctor_pharmacist';
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [form, setForm] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    specialty: '',
    job_title: '',
    highest_qualification: '',
    years_of_experience: '',
    location: '',
    experience_summary: '',
    motivation: '',
  });
  const [cv, setCv] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const title = isDoctor ? 'دكتور أو صيدلي' : 'الوظائف';
  const heading = isDoctor
    ? 'انضم إلى فريق أطباء وصيادلة طبابة'
    : 'التوظيف في طبابة';
  const subheading = isDoctor
    ? 'قدّم طلبك للانضمام إلى منصة طبابة كطبيب أو صيدلي.'
    : 'استكشف الوظائف الشاغرة في طبابة وقدّم طلبك بسهولة.';
  const submitLabel = isDoctor ? 'إرسال الطلب' : 'إرسال طلب التوظيف';

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      setError('حجم الملف كبير جداً. الحد الأقصى 8 ميجابايت.');
      return;
    }
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      setError('صيغة الملف يجب أن تكون PDF أو DOC أو DOCX.');
      return;
    }
    setError('');
    setCv(f);
  };

  const validate = () => {
    if (!form.full_name.trim()) return 'الاسم الكامل مطلوب';
    if (!form.phone_number.trim()) return 'رقم الهاتف مطلوب';
    if (!form.email.trim() || !form.email.includes('@')) return 'البريد الإلكتروني غير صالح';
    if (isDoctor && !form.specialty.trim()) return 'التخصص مطلوب';
    if (!isDoctor && !form.job_title.trim()) return 'الوظيفة المتقدم لها مطلوبة';
    if (!form.highest_qualification.trim()) return 'أعلى مؤهل علمي مطلوب';
    if (!form.years_of_experience.trim()) return 'سنوات الخبرة مطلوبة';
    if (!cv) return 'الرجاء إرفاق السيرة الذاتية (CV)';
    if (!consent) return 'يجب الموافقة على الإقرار للمتابعة';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('type', variant);
      fd.append('full_name', form.full_name.trim());
      fd.append('phone_number', form.phone_number.trim());
      fd.append('email', form.email.trim());
      if (isDoctor) fd.append('specialty', form.specialty.trim());
      else fd.append('job_title', form.job_title.trim());
      fd.append('highest_qualification', form.highest_qualification.trim());
      fd.append('years_of_experience', form.years_of_experience.trim());
      if (form.location.trim()) fd.append('location', form.location.trim());
      if (form.experience_summary.trim()) fd.append('experience_summary', form.experience_summary.trim());
      if (form.motivation.trim()) fd.append('motivation', form.motivation.trim());
      fd.append('consent', 'true');
      fd.append('cv', cv);
      await axios.post(`${API}/applications`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل إرسال الطلب. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white py-14 px-4 ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="max-w-xl mx-auto">
          <Card className="p-10 text-center" data-testid="application-success-card">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle2 size={36} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#0d2c54] mb-3">تم استلام طلبك</h1>
            <p className="text-gray-700 leading-relaxed" data-testid="application-success-message">
              تم استلام طلبك بنجاح. سيقوم فريق طبابة بمراجعة طلبك والتواصل معك في حال تم قبول طلبك.
            </p>
            <Link
              to="/"
              className="inline-block mt-6 text-[#1565c0] hover:underline font-bold"
              data-testid="back-home-link"
            >
              العودة إلى الصفحة الرئيسية
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b from-blue-50 to-white py-10 px-4 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#0d2c54] mb-2" data-testid="application-page-title">
            {heading}
          </h1>
          <p className="text-gray-600">{subheading}</p>
        </div>

        <Card className="p-6 md:p-8" data-testid={`application-form-${variant}`}>
          {error && (
            <Alert variant="destructive" className="mb-5" data-testid="application-error-alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <Label htmlFor="full_name">الاسم الكامل *</Label>
              <Input id="full_name" value={form.full_name} onChange={set('full_name')} required data-testid="input-full-name" className="mt-1" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone_number">رقم الهاتف *</Label>
                <Input id="phone_number" type="tel" value={form.phone_number} onChange={set('phone_number')} required data-testid="input-phone" className="mt-1" dir="ltr" />
              </div>
              <div>
                <Label htmlFor="email">البريد الإلكتروني *</Label>
                <Input id="email" type="email" value={form.email} onChange={set('email')} required data-testid="input-email" className="mt-1" dir="ltr" />
              </div>
            </div>

            {isDoctor ? (
              <div>
                <Label htmlFor="specialty">التخصص *</Label>
                <Input id="specialty" value={form.specialty} onChange={set('specialty')} required data-testid="input-specialty" className="mt-1" />
              </div>
            ) : (
              <div>
                <Label htmlFor="job_title">الوظيفة المتقدم لها *</Label>
                <Input id="job_title" value={form.job_title} onChange={set('job_title')} required data-testid="input-job-title" className="mt-1" />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="highest_qualification">أعلى مؤهل علمي *</Label>
                <Input id="highest_qualification" value={form.highest_qualification} onChange={set('highest_qualification')} required data-testid="input-qualification" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="years_of_experience">سنوات الخبرة *</Label>
                <Input id="years_of_experience" type="number" min="0" value={form.years_of_experience} onChange={set('years_of_experience')} required data-testid="input-years" className="mt-1" />
              </div>
            </div>

            <div>
              <Label htmlFor="cv">إرفاق السيرة الذاتية (CV) *</Label>
              <label htmlFor="cv" className="mt-1 flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#1e88e5] hover:bg-blue-50 transition">
                <Upload size={18} className="text-gray-500" />
                <span className="text-sm text-gray-700" data-testid="cv-file-label">
                  {cv ? cv.name : 'انقر لاختيار ملف PDF أو DOC / DOCX (الحد الأقصى 8MB)'}
                </span>
                <input
                  id="cv"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="input-cv"
                />
              </label>
            </div>

            <div className="pt-2 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-3">الحقول الاختيارية</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="location">الموقع</Label>
                  <Input id="location" value={form.location} onChange={set('location')} data-testid="input-location" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="experience_summary">نبذة مختصرة عن الخبرة</Label>
                  <Textarea id="experience_summary" value={form.experience_summary} onChange={set('experience_summary')} rows={3} data-testid="input-summary" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="motivation">لماذا ترغب بالانضمام إلى فريق طبابة؟</Label>
                  <Textarea id="motivation" value={form.motivation} onChange={set('motivation')} rows={3} data-testid="input-motivation" className="mt-1" />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox id="consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="checkbox-consent" className="mt-1" />
              <label htmlFor="consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                أقرّ بأن جميع المعلومات المقدمة صحيحة، وأوافق على مراجعة طلبي من قبل فريق طبابة.
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1e88e5] hover:bg-[#1976d2] py-6 text-lg font-bold rounded-xl"
              data-testid="submit-application-button"
            >
              {loading ? 'جاري الإرسال...' : submitLabel}
            </Button>
          </form>
        </Card>

        <p className="text-center mt-4 text-xs text-gray-500">
          <span data-testid="application-page-variant" className="hidden">{variant}</span>
          <span data-testid="application-page-header-title" className="hidden">{title}</span>
        </p>
      </div>
    </div>
  );
};

export const JoinDoctorPharmacistPage = () => <ApplicationFormPage variant="doctor_pharmacist" />;
export const JobApplicationPage = () => <ApplicationFormPage variant="job" />;

export default ApplicationFormPage;
